import React from 'react';
import type { NodeProps } from 'reactflow';
import { Handle, Position } from 'reactflow';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { CanvasNodeData } from '../types';

const TONE = '#7c3aed'; // violet for window functions

/**
 * Phase 4.5: Window Function Node.
 * Purple/violet scheme showing PARTITION BY, ORDER BY, and optional frame.
 */
export const WindowFunctionNode: React.FC<NodeProps<CanvasNodeData>> = React.memo(({ id, data, selected }) => {
  const wf = data.windowFunction;
  const functionName = wf?.function ?? data.label ?? 'WINDOW';
  const partitionBy = wf?.partitionBy ?? [];
  const orderBy = wf?.orderBy ?? [];
  const frame = wf?.frame;

  return (
    <Box
      data-id={id}
      data-kind="windowFunction"
      sx={{
        minWidth: 200,
        maxWidth: 280,
        borderRadius: 2.5,
        border: `2px solid ${alpha(TONE, selected ? 0.7 : 0.35)}`,
        background: `linear-gradient(135deg, ${alpha(TONE, 0.08)} 0%, ${alpha(TONE, 0.03)} 100%)`,
        boxShadow: selected
          ? `0 0 0 1px ${TONE}, 0 6px 18px rgba(124,58,237,0.15)`
          : `0 2px 8px rgba(15,23,42,0.06)`,
        p: 1.5,
        transition: 'all 0.15s ease-in-out',
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: TONE,
          border: '2px solid #fff',
          left: -4,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: TONE,
          border: '2px solid #fff',
          right: -4,
        }}
      />

      {/* Header */}
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: 1,
            backgroundColor: TONE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          W
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: TONE,
            fontSize: 13,
            fontFamily: 'monospace',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {functionName}
        </Typography>
      </Stack>

      {/* PARTITION BY */}
      {partitionBy.length > 0 && (
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}>
            PARTITION BY
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
            {partitionBy.map(col => (
              <Chip
                key={col}
                label={col}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  backgroundColor: alpha(TONE, 0.1),
                  color: TONE,
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* ORDER BY */}
      {orderBy.length > 0 && (
        <Box sx={{ mb: frame ? 0.75 : 0 }}>
          <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}>
            ORDER BY
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
            {orderBy.map(col => (
              <Chip
                key={col}
                label={col}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  backgroundColor: alpha('#6366f1', 0.1),
                  color: '#6366f1',
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Frame */}
      {frame && (
        <Tooltip title={frame}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              fontFamily: 'monospace',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {frame}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
});

export default WindowFunctionNode;
